import React, { useEffect, useState, useMemo, memo } from "react";
import { api } from "@/lib/api";
import { useInterval } from "@/lib/useInterval";
import {
  Cpu, MemoryStick, Wifi, Users, ListOrdered, TrendingUp, Database,
  CheckCircle2, AlertTriangle, XCircle, Activity, Server, Clock, Gauge,
} from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

/**
 * Phase P2 (2026-05-31) — Admin System Health Panel.
 *
 * Renders live cards + recharts sparklines using:
 *   GET /api/admin/health            (current snapshot, polled every 15s)
 *   GET /api/admin/health/history    (sparkline data, polled every 60s)
 *
 * Color coding per `thresholds` from backend:
 *   healthy = green; warning = amber; critical = red.
 *
 * Visibility-aware via useInterval — pauses on tab hidden.
 */
export default function SystemHealth() {
  const [snap, setSnap] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyHours, setHistoryHours] = useState(6);

  useInterval(() => {
    api.get("/admin/health").then((r) => setSnap(r.data)).catch(() => {});
  }, 15000);

  useInterval(() => {
    api.get(`/admin/health/history?hours=${historyHours}`)
      .then((r) => setHistory(r.data.items || []))
      .catch(() => {});
  }, 60000);

  if (!snap) {
    return (
      <div className="bg-white border border-border rounded-xl p-6 animate-pulse"
           data-testid="health-loading">
        <div className="h-5 w-32 bg-secondary/40 rounded mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[...Array(11)].map((_, i) => (
            <div key={i} className="h-20 bg-secondary/40 rounded" />
          ))}
        </div>
      </div>
    );
  }

  const t = snap.thresholds || {};
  const sys = snap.system, be = snap.backend, sh = snap.shoonya;
  const tr = snap.trading, mo = snap.mongo, st = snap.settlement;
  const uc = snap.uc_lc || {};

  return (
    <div className="space-y-4" data-testid="system-health-panel">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-bold flex items-center gap-2">
          <Server className="h-4 w-4 text-primary" />
          System Health
        </h2>
        <span className="text-[10px] text-muted-foreground">
          Last update: {new Date(snap.ts).toLocaleTimeString()}
        </span>
      </div>

      {/* Live cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <HealthCard label="Feed" value={sh.connected ? "LIVE" : "DOWN"}
                    severity={t.feed} icon={Wifi}
                    sub={sh.source === "shoonya" ? "Shoonya" : sh.source}
                    testid="health-feed" />
        <HealthCard label="CPU" value={`${sys.cpu_percent.toFixed(1)}%`}
                    severity={t.cpu} icon={Cpu}
                    sub={`Uptime ${fmtUptime(sys.process_uptime_seconds)}`}
                    testid="health-cpu" />
        <HealthCard label="RAM" value={`${sys.ram_percent.toFixed(1)}%`}
                    severity={t.ram} icon={MemoryStick}
                    sub={`${fmtBytes(sys.ram_used_bytes)} / ${fmtBytes(sys.ram_total_bytes)}`}
                    testid="health-ram" />
        <HealthCard label="WebSocket Clients" value={be.ws_clients}
                    severity="healthy" icon={Activity}
                    sub={`${be.ticks_per_minute.toFixed(0)} ticks/min`}
                    testid="health-ws" />
        <HealthCard label="Logged-in Users" value={be.logged_in_total}
                    severity="healthy" icon={Users}
                    sub={`Admin ${be.admin_active} · Client ${be.client_active}`}
                    testid="health-users" />
        <HealthCard label="Pending Orders" value={tr.pending_orders}
                    severity={tr.pending_orders > 100 ? "warning" : "healthy"}
                    icon={ListOrdered}
                    sub={`${tr.orders_today} orders today`}
                    testid="health-pending" />
        <HealthCard label="Open Positions" value={tr.open_positions}
                    severity="healthy" icon={TrendingUp}
                    sub={`${tr.active_clients} active clients`}
                    testid="health-positions" />
        <HealthCard label="Trades Today" value={tr.trades_today}
                    severity="healthy" icon={Activity}
                    sub={`Avg latency ${be.avg_latency_ms.toFixed(0)}ms`}
                    testid="health-trades" />
        <HealthCard label="Avg API Latency" value={`${be.avg_latency_ms.toFixed(0)}ms`}
                    severity={t.latency} icon={Clock}
                    sub={`${be.requests_per_minute.toFixed(0)} req/min`}
                    testid="health-latency" />
        <HealthCard label="MongoDB" value={mo.connected ? "OK" : "DOWN"}
                    severity={t.mongo} icon={Database}
                    sub={`${mo.latency_ms.toFixed(2)}ms ping`}
                    testid="health-mongo" />
        <HealthCard label="Last Settlement" value={st.last_status || "NONE"}
                    severity={t.settlement} icon={CheckCircle2}
                    sub={st.last_ran_at
                          ? new Date(st.last_ran_at).toLocaleDateString()
                          : "—"}
                    testid="health-settlement" />
      </div>

      {/* Shoonya REST Quota — UC/LC tier compliance (200 req/min cap) */}
      <ShoonyaQuotaPanel uc={uc} severity={t.uc_lc_quota || "healthy"} />

      {/* Historical sparkline charts */}
      <div className="bg-white border border-border rounded-xl p-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold flex items-center gap-2">
            <Activity className="h-3.5 w-3.5 text-primary" />
            Historical Charts
            <span className="text-[10px] text-muted-foreground font-normal">
              ({history.length} samples · 1-min granularity)
            </span>
          </h3>
          <div className="inline-flex rounded-md border border-border overflow-hidden text-[10px]">
            {[1, 6, 12, 24].map((h) => (
              <button key={h} onClick={() => setHistoryHours(h)}
                      data-testid={`history-${h}h`}
                      className={`px-2 py-1 ${historyHours === h ? "bg-primary text-primary-foreground" : ""}`}>
                {h}h
              </button>
            ))}
          </div>
        </div>
        {history.length < 2 ? (
          <div className="text-center py-8 text-xs text-muted-foreground"
               data-testid="history-empty">
            Charts will appear after the metrics-snapshot loop captures ≥2 samples
            (every 60 s · max 24 h retention).
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Sparkline title="CPU %" series={history} dataKey="cpu_percent"
                       color="#0ea5e9" testid="chart-cpu" />
            <Sparkline title="RAM %" series={history} dataKey="ram_percent"
                       color="#8b5cf6" testid="chart-ram" />
            <Sparkline title="WebSocket Clients" series={history}
                       dataKey="ws_clients" color="#10b981" testid="chart-ws" />
            <Sparkline title="Orders/min" series={history} dataKey="requests_per_minute"
                       color="#f59e0b" testid="chart-orders" />
            <Sparkline title="Ticks/min" series={history} dataKey="ticks_per_minute"
                       color="#ef4444" testid="chart-ticks" />
            <Sparkline title="Feed Reconnects" series={history}
                       dataKey="shoonya_reconnect_count" color="#a855f7"
                       testid="chart-reconnects" />
          </div>
        )}
      </div>
    </div>
  );
}

function HealthCard({ label, value, sub, severity, icon: Icon, testid }) {
  const styles = {
    healthy: "border-emerald-200 bg-emerald-50",
    warning: "border-amber-200 bg-amber-50",
    critical: "border-rose-200 bg-rose-50",
  }[severity || "healthy"];
  const dotClr = {
    healthy: "bg-emerald-500",
    warning: "bg-amber-500",
    critical: "bg-rose-500 animate-pulse",
  }[severity || "healthy"];
  const StatusIcon = severity === "critical" ? XCircle :
                     severity === "warning" ? AlertTriangle : CheckCircle2;
  const iconClr = severity === "critical" ? "text-rose-600" :
                  severity === "warning" ? "text-amber-600" : "text-emerald-600";
  return (
    <div className={`border rounded-lg p-3 relative ${styles}`} data-testid={testid}>
      <div className="absolute top-2 right-2 flex items-center gap-1">
        <StatusIcon className={`h-3 w-3 ${iconClr}`} />
        <span className={`h-1.5 w-1.5 rounded-full ${dotClr}`} />
      </div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="text-lg font-bold font-mono mt-1 truncate" title={String(value)}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px] text-muted-foreground truncate" title={sub}>
          {sub}
        </div>
      )}
    </div>
  );
}

// Perf (2026-06-05): memo'd so the 6 sparklines don't re-render on every 15s
// /admin/health poll. They only re-paint when their actual `series` reference
// changes (every 60s, from the /admin/health/history poll). Default memo
// equality is reference-identity which is exactly what we want: `setHistory`
// only swaps the array when the poll succeeds.
const Sparkline = memo(function Sparkline({ title, series, dataKey, color, testid }) {
  // Compute current/min/max for label
  const stats = useMemo(() => {
    const vals = series.map((p) => p[dataKey] || 0);
    if (!vals.length) return { current: 0, min: 0, max: 0 };
    return {
      current: vals[vals.length - 1],
      min: Math.min(...vals),
      max: Math.max(...vals),
    };
  }, [series, dataKey]);
  return (
    <div className="border border-border rounded-md p-2" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
          {title}
        </div>
        <div className="text-[10px] font-mono">
          {fmtNum(stats.current)}
          <span className="text-muted-foreground"> · min {fmtNum(stats.min)} · max {fmtNum(stats.max)}</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={60}>
        <LineChart data={series}
                   margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <XAxis dataKey="ts" hide />
          <YAxis hide domain={["auto", "auto"]} />
          <Tooltip
            contentStyle={{ fontSize: 10, padding: "4px 8px" }}
            labelFormatter={(v) => new Date(v).toLocaleTimeString()}
            formatter={(v) => [fmtNum(v), title]} />
          <Line type="monotone" dataKey={dataKey} stroke={color} dot={false}
                strokeWidth={1.5} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

function fmtNum(n) {
  const v = Number(n);
  if (!isFinite(v)) return "—";
  if (Math.abs(v) >= 1000) return v.toFixed(0);
  return v.toFixed(v < 1 ? 2 : 1);
}

function fmtBytes(n) {
  if (!n || n < 1024) return `${n}B`;
  const gb = n / 1024 / 1024 / 1024;
  if (gb >= 1) return `${gb.toFixed(1)}GB`;
  const mb = n / 1024 / 1024;
  return `${mb.toFixed(0)}MB`;
}

function fmtUptime(seconds) {
  const s = Number(seconds || 0);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

// ─── Shoonya REST Quota Panel — UC/LC compliance vs 200 req/min cap ─────
const SHOONYA_RPM_CAP = 200;

function ShoonyaQuotaPanel({ uc, severity }) {
  const rpm = Number(uc.requests_per_minute || 0);
  const cap = SHOONYA_RPM_CAP;
  const pct = Math.min(100, (rpm / cap) * 100);
  const bucket = Number(uc.bucket_tokens || 0);
  const bucketCap = 180;  // matches UC_LC_BUCKET_CAPACITY default
  const bucketPct = Math.min(100, (bucket / bucketCap) * 100);
  const totalEvent = uc.requests_total_event || 0;
  const totalHot = uc.requests_total_hot || 0;
  const totalCold = uc.requests_total_cold || 0;
  const hotSet = uc.hot_set_size || 0;
  const queueDepth = uc.event_queue_depth || 0;
  const drift = uc.drift_total || 0;
  const throttled = uc.throttled_total || 0;
  // Session 50 — WS depth vs REST source split for UC/LC updates
  const viaWs = uc.via_ws_total || 0;
  const viaRest = totalEvent + totalHot + totalCold;
  const totalUpdates = viaWs + viaRest;
  const wsPct = totalUpdates > 0 ? (viaWs / totalUpdates) * 100 : 0;
  const restPct = totalUpdates > 0 ? (viaRest / totalUpdates) * 100 : 0;
  // Compliance bands: ≥95% WS = ideal, 70-95% = warning, <70% = degraded.
  const wsBand = wsPct >= 95 ? "ideal"
              : wsPct >= 70 ? "warn"
              : totalUpdates === 0 ? "idle"
              : "degraded";

  const barClr = severity === "critical" ? "bg-rose-500"
               : severity === "warning"  ? "bg-amber-500"
                                          : "bg-emerald-500";
  const borderClr = severity === "critical" ? "border-rose-200 bg-rose-50"
                  : severity === "warning"  ? "border-amber-200 bg-amber-50"
                                            : "border-emerald-200 bg-emerald-50";

  return (
    <div className={`border rounded-xl p-4 ${borderClr}`}
         data-testid="shoonya-quota-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold flex items-center gap-2">
          <Gauge className="h-4 w-4 text-primary" />
          Shoonya REST Quota
          <span className="text-[10px] text-muted-foreground font-normal">
            (UC/LC loop · cap {cap}/min)
          </span>
        </h3>
        <span className="text-[10px] font-mono text-muted-foreground"
              data-testid="quota-severity">
          {severity.toUpperCase()}
        </span>
      </div>

      {/* RPM gauge */}
      <div className="space-y-3">
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
              Current load
            </span>
            <span className="font-mono text-sm" data-testid="quota-rpm">
              <span className="font-bold">{rpm.toFixed(0)}</span>
              <span className="text-muted-foreground"> / {cap} req/min</span>
              <span className="ml-2 text-xs font-semibold"
                    data-testid="quota-pct">
                ({pct.toFixed(0)}%)
              </span>
            </span>
          </div>
          <div className="h-2 bg-white/60 rounded-full overflow-hidden border border-border">
            <div className={`h-full ${barClr} transition-all duration-700 ease-out`}
                 style={{ width: `${pct}%` }}
                 data-testid="quota-rpm-bar" />
          </div>
        </div>

        {/* Token bucket gauge */}
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
              Token bucket
            </span>
            <span className="font-mono text-sm" data-testid="quota-bucket">
              <span className="font-bold">{bucket.toFixed(0)}</span>
              <span className="text-muted-foreground"> / {bucketCap} tokens</span>
            </span>
          </div>
          <div className="h-2 bg-white/60 rounded-full overflow-hidden border border-border">
            <div className="h-full bg-sky-500 transition-all duration-700 ease-out"
                 style={{ width: `${bucketPct}%` }}
                 data-testid="quota-bucket-bar" />
          </div>
        </div>

        {/* Session 50 — UC/LC Source split: WS Depth vs REST */}
        <div data-testid="uc-lc-source-card"
             className="bg-white/70 border border-border rounded-lg p-2.5">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5">
              UC/LC Source
              <span className={`text-[8px] px-1.5 py-0.5 rounded font-bold ${
                wsBand === "ideal"    ? "bg-emerald-100 text-emerald-800" :
                wsBand === "warn"     ? "bg-amber-100 text-amber-800" :
                wsBand === "degraded" ? "bg-rose-100 text-rose-800" :
                                        "bg-slate-100 text-slate-600"
              }`} data-testid="uc-lc-source-band">
                {wsBand === "ideal"    ? "OPTIMAL"   :
                 wsBand === "warn"     ? "REVIEW"    :
                 wsBand === "degraded" ? "DEGRADED"  :
                                         "IDLE"}
              </span>
            </span>
            <span className="text-[10px] font-mono text-muted-foreground"
                  data-testid="uc-lc-source-total">
              {totalUpdates.toLocaleString()} updates
            </span>
          </div>
          {/* Stacked bar */}
          <div className="h-2 bg-white/60 rounded-full overflow-hidden border border-border flex">
            <div className="h-full bg-emerald-500 transition-all duration-700 ease-out"
                 style={{ width: `${wsPct}%` }}
                 data-testid="uc-lc-source-ws-bar"
                 title={`WS Depth: ${wsPct.toFixed(1)}%`} />
            <div className="h-full bg-amber-500 transition-all duration-700 ease-out"
                 style={{ width: `${restPct}%` }}
                 data-testid="uc-lc-source-rest-bar"
                 title={`REST: ${restPct.toFixed(1)}%`} />
          </div>
          {/* Legend */}
          <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
            <div className="flex items-center gap-1.5"
                 data-testid="uc-lc-source-ws-row">
              <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" />
              <span className="text-muted-foreground">WS Depth:</span>
              <span className="font-mono font-bold">{wsPct.toFixed(0)}%</span>
              <span className="text-[9px] text-muted-foreground">({viaWs.toLocaleString()})</span>
            </div>
            <div className="flex items-center gap-1.5"
                 data-testid="uc-lc-source-rest-row">
              <span className="inline-block w-2 h-2 rounded-sm bg-amber-500" />
              <span className="text-muted-foreground">REST:</span>
              <span className="font-mono font-bold">{restPct.toFixed(0)}%</span>
              <span className="text-[9px] text-muted-foreground">({viaRest.toLocaleString()})</span>
            </div>
          </div>
        </div>

        {/* Per-tier breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 pt-1">
          <QuotaStat label="HOT set" value={hotSet} testid="quota-hot-set" />
          <QuotaStat label="Event queue" value={queueDepth}
                     testid="quota-event-queue" />
          <QuotaStat label="Event total" value={totalEvent}
                     testid="quota-total-event" />
          <QuotaStat label="HOT total" value={totalHot}
                     testid="quota-total-hot" />
          <QuotaStat label="COLD total" value={totalCold}
                     testid="quota-total-cold" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <QuotaStat label="UC/LC drift detected" value={drift}
                     testid="quota-drift" />
          <QuotaStat label="Throttled (bucket dry)" value={throttled}
                     emphasis={throttled > 0 ? "warning" : "default"}
                     testid="quota-throttled" />
        </div>
      </div>
    </div>
  );
}

function QuotaStat({ label, value, emphasis, testid }) {
  const cls = emphasis === "warning" ? "text-amber-700 font-bold" : "";
  return (
    <div className="bg-white/70 border border-border rounded px-2 py-1"
         data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`font-mono text-sm ${cls}`}>
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}
